import HttpClient from "./httpClient";
export interface CreditPolicy {
  count_root_usage: boolean;
  ramp: { month: number; credits: number }[];
  rollover: "days" | "term";
  rollover_days: number;
  chat_rate: number;
  automatic_ticket_rate: number;
  technician_ticket_rate: number;
  workflow_rates: Record<string, number>;
  completed_statuses: Record<string, string[]>;
}
export interface CreditAccount { contract_start: string; contract_end: string; }
export interface BillingSummaryResponse {
  timezone: string;
  activation: { starts_at: string; ends_at: string; already_issued: boolean; grant_units: number } | null;
  enabled: boolean;
  effective: boolean;
  account: CreditAccount | null;
  policy: CreditPolicy;
  period: { id: number; starts_at: string; ends_at: string; budget_units: number; used_units: number } | null;
  balances: { available_units: number; held_units: number };
  grants: { id: number; available_units: number; held_units: number; expires_at: string }[];
  periods: { id: number; starts_at: string; ends_at: string }[];
  clients: { customer_id: number | null; name: string | null; kind: string; units: number }[];
  breakdown: { kind: string; origin: string; completed: number; units: number }[];
}
export type CreditCategory = "chat" | "ticket" | "workflow" | "allowance" | "addition";
export interface CreditLedgerFilter { period_id?: string; customer_id?: string; categories?: CreditCategory[]; search?: string; }
const ledgerQuery = ({ categories, ...filters }: CreditLedgerFilter) => ({ ...filters, categories: categories?.join(",") });
export interface CreditMovement { ai_thread_id: number | null; customer_name: string | null; workflow_key: string | null; id: number; created_at: string; kind: string; work_kind: string | null; origin: string | null; ticket_id: string | null; customer_id: number | null; available_delta: number; held_delta: number; reason: string; }
export interface CreditSettings { defaults: CreditPolicy; overrides: Partial<CreditPolicy>; effective: CreditPolicy; }
export interface BillingWorkflowOption { key: string; name: string; client_name: string | null; }
export interface BillingStatusOption { id: string; label: string; }
export interface BillingStatusOptions {
  platform: { id: number; name: string } | null;
  statuses: BillingStatusOption[];
}
class BillingApi extends HttpClient {
  getWorkflowOptions = (mspId: number): Promise<{ workflows: BillingWorkflowOption[] }> => this.get("/api/billing/workflows/" + mspId);
  getStatusOptions = (mspId: number): Promise<BillingStatusOptions> => this.get("/api/billing/statuses/" + mspId);
  getSummary = async (opts: { mspId?: number | null } = {}): Promise<BillingSummaryResponse> => this.get("/api/billing/summary", undefined, opts.mspId ? { mspId: opts.mspId } : undefined);
  getLedger = async (mspId: number, before?: number, filters: CreditLedgerFilter = {}): Promise<{ rows: CreditMovement[] }> => this.get("/api/billing/ledger", undefined, { mspId, ...ledgerQuery(filters), ...(before ? { before } : {}) });
  getSettings = async (mspId: number): Promise<CreditSettings> => this.get(`/api/billing/settings/${mspId}`);
  saveSettings = async (mspId: number, value: Partial<CreditPolicy>): Promise<CreditSettings> => this.put(`/api/billing/settings/${mspId}`, undefined, value);
  saveAccount = async (mspId: number, value: CreditAccount & { timezone: string }): Promise<BillingSummaryResponse> => this.put(`/api/billing/accounts/${mspId}`, undefined, value);
  setEnabled = async (mspId: number, enabled: boolean): Promise<BillingSummaryResponse> => this.put(`/api/billing/accounts/${mspId}/enabled`, undefined, { enabled });
  requestCredits = async (mspId: number, value: { credits: number; reason: string }): Promise<{ sent: boolean; email_sent: boolean }> => this.post(`/api/billing/accounts/${mspId}/requests`, undefined, value);
  addCredits = async (mspId: number, value: { request_id: string; credits: number; expires_at: string; reason: string }): Promise<unknown> => this.post(`/api/billing/accounts/${mspId}/additions`, undefined, value);
  exportLedger = (mspId: number, filters: CreditLedgerFilter = {}) => this.getBlob("/api/billing/ledger/export", { mspId, ...ledgerQuery(filters) });
}
const billingApi = new BillingApi();
export default billingApi;
